import { Component } from '@theme/component';
import { fetchConfig, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  /** @type {MutationObserver | undefined} */
  #subscriptionsObserver;
  /** @type {number[]} */
  #subscriptionMountTimeouts = [];

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    this.#mountSubscriptionsWidget();
    requestAnimationFrame(() => this.#mountSubscriptionsWidget());

    const subscriptionsScope = this.#getSubscriptionsScope();
    if (subscriptionsScope) {
      this.#subscriptionsObserver = new MutationObserver(() => this.#mountSubscriptionsWidget());
      this.#subscriptionsObserver.observe(subscriptionsScope, { childList: true, subtree: true });
    }

    for (const delay of [500, 1500, 3000]) {
      this.#subscriptionMountTimeouts.push(window.setTimeout(() => this.#mountSubscriptionsWidget(), delay));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#subscriptionsObserver?.disconnect();
    for (const timeoutId of this.#subscriptionMountTimeouts) {
      clearTimeout(timeoutId);
    }
    this.#subscriptionMountTimeouts = [];
    this.#abortController.abort();
  }

  #getSubscriptionsScope() {
    return (
      this.closest('.product-details') ??
      this.closest('.shopify-section') ??
      this.closest('.group-block-content')
    );
  }

  #mountSubscriptionsWidget() {
    const slot = this.querySelector('[data-rumu-subscriptions-slot]');
    if (!slot) return;

    const scope = this.#getSubscriptionsScope();
    const widgets = scope?.querySelectorAll('.shopify_subscriptions_app_container');
    if (!widgets?.length) return;

    let widget = null;
    widgets.forEach((candidate) => {
      if (!(candidate instanceof HTMLElement)) return;
      if (slot.contains(candidate)) return;
      if (candidate.closest('[hidden]')) return;
      widget = candidate;
    });

    if (!widget) return;

    const blockWrapper =
      widget.closest('.shopify-block') ??
      scope?.querySelector('[id*="subscriptions_app_block"]');

    slot.appendChild(widget);

    blockWrapper?.setAttribute('hidden', '');

    this.#enhanceSubscriptionsUI(widget);
  }

  /**
   * @param {HTMLElement} widget
   */
  #enhanceSubscriptionsUI(widget) {
    const oneTimeRadio = widget.querySelector('input[data-radio-type="one_time_purchase"]');
    const sellingPlanRadio = widget.querySelector('input[data-radio-type="selling_plan"]');
    const formButtons = this.querySelector('.product-form-buttons');
    const atcComponent = this.refs.addToCartButtonContainer;

    if (!oneTimeRadio || !sellingPlanRadio || !formButtons || !atcComponent) return;

    this.classList.add('rumu-has-subscriptions');
    widget.classList.add('rumu-subscriptions-widget--hidden');

    const cartActions = formButtons.querySelector('[data-rumu-purchase-cart]');
    const subscribeRow = formButtons.querySelector('[data-rumu-subscribe-row]');
    const subscribeLeading = formButtons.querySelector('[data-rumu-subscribe-leading]');
    const subscribeTrailing = formButtons.querySelector('[data-rumu-subscribe-trailing]');

    if (!cartActions || !subscribeRow || !subscribeLeading || !subscribeTrailing) return;

    formButtons.querySelector('[data-rumu-purchase-actions]')?.remove();

    const quantitySelector = cartActions.querySelector('quantity-selector-component, .quantity-selector');
    if (quantitySelector && !subscribeLeading.contains(quantitySelector)) {
      subscribeLeading.appendChild(quantitySelector);
    }

    let oneTimeButton = cartActions.querySelector('[data-rumu-purchase-one-time]');
    let subscribeButton = subscribeTrailing.querySelector('[data-rumu-purchase-subscribe]');

    if (!oneTimeButton) {
      oneTimeButton = document.createElement('button');
      oneTimeButton.type = 'button';
      oneTimeButton.className = 'button button-secondary rumu-purchase-action rumu-purchase-action--one-time';
      oneTimeButton.setAttribute('data-rumu-purchase-one-time', '');
      cartActions.appendChild(oneTimeButton);
    }

    if (!subscribeButton) {
      subscribeButton = document.createElement('button');
      subscribeButton.type = 'button';
      subscribeButton.className = 'button button-secondary rumu-purchase-action rumu-purchase-action--subscribe';
      subscribeButton.setAttribute('data-rumu-purchase-subscribe', '');
      subscribeTrailing.appendChild(subscribeButton);
    }

    subscribeRow.hidden = false;

    oneTimeButton.textContent = this.#getAddToCartLabel(atcComponent);
    subscribeButton.textContent = this.#getSubscribeButtonLabel(sellingPlanRadio);

    atcComponent.setAttribute('hidden', '');

    if (!subscribeRow.dataset.rumuBound) {
      subscribeRow.dataset.rumuBound = 'true';
      oneTimeButton.addEventListener('click', () => this.#purchaseWithOption(oneTimeRadio));
      subscribeButton.addEventListener('click', () => this.#purchaseWithOption(sellingPlanRadio));
    }

    this.#syncPurchaseActionButtonsState();
  }

  /**
   * @param {HTMLInputElement} radio
   */
  #purchaseWithOption(radio) {
    if (!radio || radio.disabled) return;

    radio.checked = true;
    radio.dispatchEvent(new Event('input', { bubbles: true }));
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    radio.click();

    const form = this.querySelector('form');
    if (!form) return;

    this.refs.addToCartButtonContainer?.animateAddToCart?.();
    form.requestSubmit();
  }

  /**
   * @param {AddToCartComponent | undefined} atcComponent
   */
  #getAddToCartLabel(atcComponent) {
    return (
      atcComponent?.querySelector('.add-to-cart-text__content')?.textContent?.trim() ||
      Theme.translations?.addToCart ||
      'Add to cart'
    );
  }

  /**
   * @param {HTMLInputElement} sellingPlanRadio
   */
  #getSubscribeButtonLabel(sellingPlanRadio) {
    const discount = this.#getSubscriptionDiscount(sellingPlanRadio);
    return discount ? `Subscribe ${discount}% OFF` : 'Subscribe';
  }

  /**
   * @param {HTMLInputElement} radio
   */
  #getSubscriptionDiscount(radio) {
    const labelText = radio.closest('label')?.textContent || radio.getAttribute('aria-label') || '';
    const percentMatch = labelText.match(/(\d+)\s*%/);
    if (percentMatch) return percentMatch[1];

    const comparePrice = this.#parsePrice(radio.getAttribute('data-variant-compare-at-price'));
    const price = this.#parsePrice(radio.getAttribute('data-variant-price'));

    if (comparePrice && price && comparePrice > price) {
      return String(Math.round((1 - price / comparePrice) * 100));
    }

    return null;
  }

  /**
   * @param {string | null} value
   */
  #parsePrice(value) {
    if (!value) return null;

    const normalized = value.replace(/[^\d,.-]/g, '').replace(',', '.');
    const amount = Number.parseFloat(normalized);

    return Number.isNaN(amount) ? null : amount;
  }

  #syncPurchaseActionButtonsState() {
    const originalButton = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (!originalButton) return;

    const isDisabled = originalButton.disabled;
    const oneTimeButton = this.querySelector('[data-rumu-purchase-one-time]');
    const subscribeButton = this.querySelector('[data-rumu-purchase-subscribe]');

    oneTimeButton?.toggleAttribute('disabled', isDisabled);
    subscribeButton?.toggleAttribute('disabled', isDisabled);

    if (oneTimeButton) {
      oneTimeButton.textContent = this.#getAddToCartLabel(this.refs.addToCartButtonContainer);
    }

    const sellingPlanRadio = this.querySelector('input[data-radio-type="selling_plan"]');
    if (subscribeButton && sellingPlanRadio instanceof HTMLInputElement) {
      subscribeButton.textContent = this.#getSubscribeButtonLabel(sellingPlanRadio);
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          const accentColor =
            this.refs.addToCartButtonContainer?.getAttribute('data-variant-accent-color') || '';

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
              ...(accentColor ? { accentColor } : {}),
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    requestAnimationFrame(() => {
      this.#mountSubscriptionsWidget();
      this.#syncPurchaseActionButtonsState();
    });

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    const formFragment = event.detail.data.html;
    if (formFragment && addToCartButtonContainer && this.dataset.productId) {
      const newAtc = formFragment.querySelector(
        `product-form-component[data-product-id="${this.dataset.productId}"] add-to-cart-component`
      );
      if (newAtc) {
        const accent = newAtc.getAttribute('data-variant-accent-color');
        if (accent) {
          addToCartButtonContainer.setAttribute('data-variant-accent-color', accent);
        } else {
          addToCartButtonContainer.removeAttribute('data-variant-accent-color');
        }
      }
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
    this.#syncPurchaseActionButtonsState();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
